/**
 * Unit 2.4 · IMPL.A: the client workspace shell with header + URL-backed tabs.
 *
 * Bead: insurescope-11zkc.3.4.3.1. Design contract:
 * docs/design-briefs/2026-06-03-client-detail.md. Tab + folder are PATH
 * segments (full parity with the document library's
 * documents/folder/:folderId grammar; plan
 * docs/plans/2026-06-08-002-refactor-client-hub-path-routing-plan.md): the
 * tab is `…/clients/:id/library` | `…/conversations` (derived from the
 * pathname), and the folder is the `:folderId` route param under
 * `…/library/folder/:folderId`. `…/clients/:id` redirects to `…/library`.
 * One ClientDetail renders at each leaf; React reconciles across the
 * sibling routes, so a tab/folder navigation never remounts the header.
 * The Library pane is Unit 2.5's ClientLibraryTab; the Conversations pane
 * is Unit 2.6's ClientConversationsTab.
 *
 * The `:folderId` is the durable workspace-folder state. Validation walks
 * the folders cache's parent chain to the client root and GATES on that
 * cache being loaded (no fallback flash); a foreign/unknown/non-numeric id
 * falls back to `…/library` silently (replace), while a mid-session
 * deletion (404 on a cache-validated folder) toasts. The Library tab nav
 * targets the bare `…/library` (root), so returning from Conversations
 * never restores a folder (no tab-switch memory). Brief:
 * docs/design-briefs/2026-06-05-client-workspace-folder-view.md.
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useDialogs } from "@toolpad/core";
import { isAxiosError } from "axios";
import { FolderOpen, MessageSquare, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { toast } from "sonner";

import { getClient, getLibraryFolders } from "@/api";
import { ChatSheet } from "@/components/assistant-ui/chat-sheet";
import { CreateScopeDialog } from "@/components/frontend/dialogs/create-scope/CreateScopeDialog";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { HOME_ORG_PATH } from "@/config/constants";
import { useOrganization } from "@/contexts/frontend/organization/useOrganization";
import { useBreadcrumb } from "@/contexts/shared/breadcrumb";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";
import { UploadDialog } from "@/pages/frontend/documents/components/UploadDialog";
import {
    getAncestorChain,
    getClientSubtreeFolders,
} from "@/pages/frontend/documents/utils/folderTreeUtils";

import {
    CLIENT_CONVERSATIONS_KEY,
    ClientConversationsTab,
} from "./components/ClientConversationsTab";
import { ClientDetailHeader } from "./components/ClientDetailHeader";
import { ClientLibraryTab } from "./components/ClientLibraryTab";
import { StateShell } from "./components/ClientListEmptyState";
import { useResolveClientRootFolder } from "./hooks/useResolveClientRootFolder";

type DetailTab = "library" | "conversations";

export default function ClientDetail() {
    const { clientId, folderId: folderIdParam } = useParams<{
        clientId: string;
        folderId: string;
    }>();
    const location = useLocation();
    const navigate = useNavigate();
    const { setBreadcrumbs } = useBreadcrumb();
    const queryClient = useQueryClient();

    const id = Number(clientId);
    const validId = Number.isFinite(id);
    // Every workspace navigation targets this base; the tab and folder are
    // appended path segments.
    const clientPath = `/organizations/clients/${id}`;
    const {
        data: client,
        isPending,
        isError,
        error,
        refetch,
    } = useQuery({
        queryKey: ["client", id],
        queryFn: () => getClient(id),
        enabled: validId,
    });

    // Header Upload verb (Unit 3.2; R4/R5): resolve the client's root folder,
    // then open the shared UploadDialog scoped to the client subtree. The
    // current folder is the root, so the folder select shows empty and an
    // empty submit lands in the client root (never the org library root).
    const [uploadFolderId, setUploadFolderId] = useState<number | null>(null);
    const resolveRootFolder = useResolveClientRootFolder(id, {
        onResolved: (folder) => setUploadFolderId(folder.id),
        errorFallback: "Couldn't start the upload",
    });

    const handleUploadComplete = () => {
        // The header counts (documents, pages, last activity) come from
        // the client query; the Library tab listens to the library list.
        queryClient.invalidateQueries({ queryKey: ["client", id] });
        queryClient.invalidateQueries({ queryKey: ["frontend-library-list"] });
    };

    // Header Ask Polly verb (Unit 3.3): a client-scoped ChatSheet; the
    // sheet stages the client so the conversation is tagged at birth.
    // The Conversations tab's CTAs route through the same handler
    // (Unit 2.6's single-entry-point rule).
    const [askPollyOpen, setAskPollyOpen] = useState(false);
    const handleAskPollyClose = () => {
        setAskPollyOpen(false);
        // A sheet-created conversation must appear in the mounted tab
        // without a manual refresh (design brief: freshness after Ask
        // Polly). Prefix key, so every q variant goes stale together.
        queryClient.invalidateQueries({
            queryKey: [CLIENT_CONVERSATIONS_KEY, id],
        });
    };

    // Header New Scope verb (Unit 3.4): the global create-scope dialog
    // with the client preset, so the scope is tagged at birth. The industry
    // default mirrors the topbar CreateMenu's payload so this entry point
    // doesn't silently lose the org-industry prefill.
    const dialogs = useDialogs();
    const { organization } = useOrganization();
    const handleNewScope = () => {
        if (!client) return;
        dialogs.open(CreateScopeDialog, {
            organizationId: client.organization_id,
            organizationIndustryId: organization.industry_id,
            clientId: id,
        });
    };

    useDocumentTitle(client ? client.name : "Clients");

    useEffect(() => {
        setBreadcrumbs([
            { label: "Home", path: HOME_ORG_PATH },
            { label: "Clients", path: "/organizations/clients" },
            { label: client?.name ?? "…" },
        ]);
        // eslint-disable-next-line react-hooks/exhaustive-deps -- name-keyed
    }, [client?.name]);

    // Tab is a path segment: `…/conversations` vs everything else
    // (`…/library`, `…/library/folder/:id`) → library. Mirrors
    // DocumentLibraryContent's pathname sniff.
    const tab: DetailTab = location.pathname.endsWith("/conversations")
        ? "conversations"
        : "library";

    // The Library option targets the bare `…/library` (root): switching away
    // and back never restores a folder (no tab-switch memory). Push so Back
    // returns to the prior tab.
    const handleTabChange = (value: string) => {
        navigate(`${clientPath}/${value}`);
    };

    // --- :folderId workspace state (path routing) ---------------------------
    // Same key as the main library / the tab (shared cache); needed here to
    // validate deep links before ANY listing fetch fires.
    const { data: foldersData } = useQuery({
        queryKey: ["frontend-library-folders"],
        queryFn: () => getLibraryFolders(),
        enabled: validId,
    });
    const folders = foldersData?.folders;
    const rootFolderId = client?.root_folder_id ?? null;

    // The tab must not fetch until :folderId is validatable (client root id
    // + the folders cache): a premature root fetch is the fallback flash.
    const folderStateReady = client !== undefined && folders !== undefined;

    const requestedFolderId =
        folderIdParam && /^\d+$/.test(folderIdParam)
            ? Number(folderIdParam)
            : null;

    // Valid iff the folder exists and its ancestor chain terminates at THIS
    // client's root: a foreign or library folder can never become the
    // workspace location.
    let validatedFolderId: number | null = null;
    if (
        folderStateReady &&
        folders !== undefined &&
        requestedFolderId !== null &&
        rootFolderId !== null
    ) {
        const target = folders.find((f) => f.id === requestedFolderId);
        if (target) {
            const chain = getAncestorChain(folders, target);
            if (chain[0]?.id === rootFolderId) {
                validatedFolderId = requestedFolderId;
            }
        }
    }
    const folderParamInvalid =
        folderStateReady &&
        folderIdParam !== undefined &&
        validatedFolderId === null;

    // Foreign/unknown/garbage deep links fall back to root SILENTLY (the
    // user never had this folder; a bad link is not their mistake). The
    // toast is reserved for the mid-session-deletion case below. Replace,
    // not push: cleaning a broken URL is not a navigation.
    useEffect(() => {
        if (!folderParamInvalid) return;
        navigate(`${clientPath}/library`, { replace: true });
    }, [folderParamInvalid, clientPath, navigate]);

    // Folder navigation pushes history entries (R5: back/forward keep
    // place); the root is the param-less URL (`…/library`), never
    // `…/library/folder/<rootId>`.
    const handleFolderNavigate = (folderId: number | null) => {
        if (folderId === null || folderId === rootFolderId) {
            navigate(`${clientPath}/library`);
        } else {
            navigate(`${clientPath}/library/folder/${folderId}`);
        }
    };

    // Mid-session deletion: the listing 404'd on a folder the cache had
    // validated. Toast (locked copy), land on root, heal the stale cache.
    const handleFolderUnavailable = () => {
        toast("This folder no longer exists");
        navigate(`${clientPath}/library`, { replace: true });
        queryClient.invalidateQueries({
            queryKey: ["frontend-library-folders"],
        });
    };

    // A malformed id can never resolve; treat it as not-found instead of
    // stranding the user on a permanent skeleton (enabled:false keeps
    // isPending true forever).
    const isNotFound =
        !validId ||
        (isError && isAxiosError(error) && error.response?.status === 404) ||
        (!isPending && !isError && !client);
    const isTransientError = isError && !isNotFound;

    if (isNotFound || isTransientError) {
        return (
            <StateShell
                className="p-12"
                icon={<Users className="text-muted-foreground size-5" />}
                headline={
                    isNotFound
                        ? "Client not found"
                        : "Something went wrong loading this client"
                }
            >
                <p className="text-muted-foreground max-w-sm text-sm">
                    {isNotFound
                        ? "It may have been deleted, or the link is stale."
                        : "The client is still there; the request just failed."}
                </p>
                <span className="flex items-center gap-2">
                    {isTransientError && (
                        <Button variant="ghost" onClick={() => refetch()}>
                            Try again
                        </Button>
                    )}
                    <Button
                        variant="outline"
                        render={<Link to="/organizations/clients" />}
                    >
                        ← All clients
                    </Button>
                </span>
            </StateShell>
        );
    }

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-2">
            <ClientDetailHeader
                client={client}
                isLoading={isPending}
                onUploadDocument={() => resolveRootFolder.mutate()}
                onNewScope={handleNewScope}
                onAskPolly={() => setAskPollyOpen(true)}
            />

            <UploadDialog
                open={uploadFolderId !== null}
                onClose={() => setUploadFolderId(null)}
                folderId={uploadFolderId}
                isKnowledgeBase={false}
                allowSpreadsheets
                folders={getClientSubtreeFolders(folders ?? [], rootFolderId)}
                rootFolderId={rootFolderId}
                defaultClient={client ?? null}
                onUploadComplete={handleUploadComplete}
            />

            <ChatSheet
                open={askPollyOpen}
                onClose={handleAskPollyClose}
                clientId={id}
            />

            <div className="border-b px-4">
                <Tabs value={tab} onValueChange={handleTabChange}>
                    <TabsList variant="line">
                        <TabsTrigger value="library">
                            <FolderOpen className="size-4" />
                            Library
                        </TabsTrigger>
                        <TabsTrigger value="conversations">
                            <MessageSquare className="size-4" />
                            Conversations
                        </TabsTrigger>
                    </TabsList>
                </Tabs>
            </div>

            {/* The pane (not the page) is the scrollport, so the header and
            tabs stay put while content scrolls under them. It declares
            [container-type:size] so DocumentDetailLayout's sticky panel can
            size itself to it via 100cqh (the layout's host contract). No
            right padding, like DocumentLibraryLayout's scrollport, so the
            detail panel reaches the screen edge; the layout's list column
            owns pr-4, and the conversations pane adds its own. */}
            <div className="[container-type:size] flex min-h-0 flex-1 flex-col overflow-y-auto pt-4 pb-4 pl-4">
                {tab === "library" ? (
                    <div
                        data-testid="library-pane"
                        className="flex min-h-0 flex-1 flex-col"
                    >
                        <ClientLibraryTab
                            clientId={id}
                            rootFolderId={rootFolderId}
                            currentFolderId={validatedFolderId ?? rootFolderId}
                            folderStateReady={folderStateReady}
                            onFolderNavigate={handleFolderNavigate}
                            onFolderUnavailable={handleFolderUnavailable}
                        />
                    </div>
                ) : (
                    <div data-testid="conversations-pane" className="pr-4">
                        <ClientConversationsTab
                            clientId={id}
                            onAskPolly={() => setAskPollyOpen(true)}
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
